import { useEffect, useState } from 'react'
import { DATA, OPTIONAL_FIELDS } from './helpers'

// Constante con los mensajes por defecto de error para cada campo/tipo de error
const FIELD_ERROR_MESSAGES = {
  required: 'Este campo es obligatorio.',
  maxLength: (field, max) => `El campo ${field} no puede superar los ${max} caracteres.`,
  email: 'Ingrese un email válido.',
  noCommas: 'Altura y peso no pueden contener comas ni puntos.' // Actualizado
}

const FIELD_LABELS = {
  nombre: 'Nombre',
  apellido: 'Apellido',
  calle: 'Calle',
  localidad: 'Localidad',
  codigoAfiliado: 'Código de afiliado',
  informacionAdicional: 'Información adicional',
  email: 'Correo electrónico',
  altura: 'Altura',
  peso: 'Peso'
}

export const useRequiered = (phase) => {
  const [isValid, setIsValid] = useState(null)
  const [buttonAvailable, setButtonAvailable] = useState(null)
  const [firstCheckByField, setFirstCheckByField] = useState(() =>
    phase && DATA[phase]
      ? Object.keys(DATA[phase]).reduce((acc, key) => {
          acc[key] = true
          return acc
        }, {})
      : {}
  )
  const [patientData, setPatientData] = useState(null)
  const [dataError, setDataError] = useState(DATA[phase])
  const [errorMessages, setErrorMessages] = useState({})

  useEffect(() => {
    if (phase) {
      setDataError(DATA[phase])
      setIsValid(null)
      setButtonAvailable(null)
      setErrorMessages({})
      setFirstCheckByField(
        Object.keys(DATA[phase] || {}).reduce((acc, key) => {
          acc[key] = true
          return acc
        }, {})
      )
    }
  }, [phase])

  // Lógica de validaciones y mensajes por campo
  const maxLengths = {
    dni: 255,
    genero: 50,
    nombre: 255,
    apellido: 255,
    calle: 255,
    email: 255,
    telefono: 50,
    localidad: 255,
    tipoSeguridadSocial: 255,
    codigoAfiliado: 255,
    informacionAdicional: 300 // No existe en DB, mantenido por lógica de frontend
  }

  // Cambiado: ahora también prohibe puntos
  const hasNoCommas = (value) => {
    const val = String(value || '')
    return !val.includes(',') && !val.includes('.')
  }

  const checkField = (key, data) => {
    const label = FIELD_LABELS[key] || key

    // Utilizamos el phase recibido en el hook para decidir qué validar
    if (phase === 'PhaseOne') {
      // Validar todo excepto informacionAdicional, altura y peso
      switch (key) {
        case 'nombre':
        case 'apellido':
        case 'calle':
        case 'localidad':
        case 'codigoAfiliado':
          if (data[key] && maxLengths[key]) {
            if (String(data[key]).length > maxLengths[key]) {
              return { valid: false, msg: FIELD_ERROR_MESSAGES.maxLength(label, maxLengths[key]) }
            }
          }
          break
        case 'email':
          if (data[key] && maxLengths[key]) {
            const email = String(data[key])
            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
            if (email.length > maxLengths[key]) {
              return { valid: false, msg: FIELD_ERROR_MESSAGES.maxLength(label, maxLengths[key]) }
            }
            if (!emailRegex.test(email)) {
              return { valid: false, msg: FIELD_ERROR_MESSAGES.email }
            }
          }
          break
        default:
          // No se valida nada específico para otros campos en PhaseOne
          break
      }
    } else if (phase === 'PhaseTwo') {
      // Unicamente validar informacionAdicional, altura y peso
      switch (key) {
        case 'informacionAdicional':
          if (data[key] && maxLengths[key]) {
            if (String(data[key]).length > maxLengths[key]) {
              return { valid: false, msg: FIELD_ERROR_MESSAGES.maxLength(label, maxLengths[key]) }
            }
          }
          break
        case 'altura':
        case 'peso':
          if (data[key]) {
            if (!hasNoCommas(data[key])) {
              return { valid: false, msg: FIELD_ERROR_MESSAGES.noCommas }
            }
          }
          break
        default:
          // No se valida nada para otros campos en PhaseTwo
          break
      }
    } else if (phase === 'PatientDataForm') {
      // Como estaba antes, toda la logica de validacion completa para este formulario
      switch (key) {
        case 'nombre':
        case 'apellido':
        case 'calle':
        case 'localidad':
        case 'codigoAfiliado':
        case 'informacionAdicional':
          if (data[key] && maxLengths[key]) {
            if (String(data[key]).length > maxLengths[key]) {
              return { valid: false, msg: FIELD_ERROR_MESSAGES.maxLength(label, maxLengths[key]) }
            }
          }
          break
        case 'email':
          if (data[key] && maxLengths[key]) {
            const email = String(data[key])
            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
            if (email.length > maxLengths[key]) {
              return { valid: false, msg: FIELD_ERROR_MESSAGES.maxLength(label, maxLengths[key]) }
            }
            if (!emailRegex.test(email)) {
              return { valid: false, msg: FIELD_ERROR_MESSAGES.email }
            }
          }
          break
        case 'altura':
        case 'peso':
          if (data[key]) {
            if (!hasNoCommas(data[key])) {
              return { valid: false, msg: FIELD_ERROR_MESSAGES.noCommas }
            }
          }
          break
        default:
          break
      }
    }
    return { valid: true, msg: '' }
  }

  const checkRequired = (data, noSocialSecurity, button = false, clicked = false, fieldName = null, referenceData = null) => {
    // Si se está validando un campo específico, preservar los errores existentes
    // Si es una validación completa (clicked), empezar desde cero
    const localDataError = clicked ? {} : { ...dataError }
    const localErrorMessages = clicked ? {} : { ...errorMessages }
    const updatedFirstCheckByField = { ...firstCheckByField }

    // Si estamos validando un campo específico, solo procesar ese campo
    // Si es una validación completa, procesar todos los campos
    const fieldsToValidate = fieldName !== null && !clicked ? [fieldName] : Object.keys(data)

    const dataToCheck = referenceData || patientData

    fieldsToValidate.forEach(key => {
      let isFieldRequired = !OPTIONAL_FIELDS[key]

      if (key === 'codigoAfiliado') {
        isFieldRequired = !noSocialSecurity
      }

      // Si se está validando un campo específico en onBlur, mostrar error solo para ese campo
      const isBlurField = fieldName === key
      const shouldShowError = clicked || isBlurField

      if ((data[key] === '' && isFieldRequired) && (dataToCheck ? dataToCheck[key] === null : true)) {
        if (shouldShowError) {
          localDataError[key] = true
          localErrorMessages[key] = FIELD_ERROR_MESSAGES.required
        }
        updatedFirstCheckByField[key] = false
      } else if (data[key] !== '') {
        const fieldCheck = checkField(key, data)
        if (!fieldCheck.valid) {
          if (shouldShowError) {
            localDataError[key] = true
            localErrorMessages[key] = fieldCheck.msg
          }
        } else {
          localDataError[key] = false
          localErrorMessages[key] = ''
        }
        updatedFirstCheckByField[key] = false
      } else {
        localDataError[key] = false
        localErrorMessages[key] = ''
        updatedFirstCheckByField[key] = false
      }
    })

    // Calcular hasErrors basándose en todos los campos (incluyendo los preservados)
    let hasErrors = false
    Object.keys(data).forEach(key => {
      let isFieldRequired = !OPTIONAL_FIELDS[key]
      if (key === 'codigoAfiliado') {
        isFieldRequired = !noSocialSecurity
      }

      if ((data[key] === '' && isFieldRequired) && (dataToCheck ? dataToCheck[key] === null : true)) {
        hasErrors = true
      } else if (data[key] !== '') {
        const fieldCheck = checkField(key, data)
        if (!fieldCheck.valid) {
          hasErrors = true
        }
      }
    })

    const localButtonAvailable = !hasErrors
    const localIsValid = clicked && !hasErrors

    setFirstCheckByField(updatedFirstCheckByField)

    setDataError(prevDataError => ({ ...prevDataError, ...localDataError }))
    setErrorMessages(prevMsgs => ({ ...prevMsgs, ...localErrorMessages }))

    if (clicked) {
      setIsValid(localIsValid)
    } else {
      setIsValid(prevIsValid => prevIsValid === true ? null : prevIsValid)
    }
    setButtonAvailable(localButtonAvailable)
  }

  return {
    isValid,
    dataError,
    setPatientData,
    checkRequired,
    buttonAvailable,
    setButtonAvailable,
    errorMessages
  }
}
